<template>
  <div
    :class="[$style.toggleWrapper, $style[variant]]"
    :data-disabled="disabled || undefined"
  >
    <label
      :class="{
        [$style.toggleOption]: true,
        [$style.checked]: pickedLeft,
        [$style.disabled]: disabled,
      }"
    >
      <div>
        <input
          v-model="picked"
          type="radio"
          :value="props.firstOption.value"
          style="display: none"
          :disabled="disabled"
          @blur="$emit('blur', $event)"
          @input="setValue($event)"
        />
        <template v-if="props.firstOption.icon">
          <Icon :icon="props.firstOption.icon" />
        </template>
        {{ props.firstOption.label }}
      </div>
    </label>

    <label
      :class="{
        [$style.toggleOption]: true,
        [$style.checked]: pickedRight,
        [$style.disabled]: disabled,
      }"
    >
      <div>
        <input
          v-model="picked"
          type="radio"
          :value="props.secondOption.value"
          style="display: none"
          :disabled="disabled"
          @blur="$emit('blur', $event)"
          @input="setValue($event)"
        />
      </div>
      <template v-if="props.secondOption.icon">
        <Icon :icon="props.secondOption.icon" />
      </template>
      {{ props.secondOption.label }}
    </label>
  </div>
</template>
<script setup lang="ts">
import { ref, watch, computed } from 'vue';

type Option = {
  label: string;
  value: unknown;
  icon?: string;
};

type Props = {
  firstOption: Option;
  secondOption: Option;
  modelValue: unknown;
  disabled?: boolean;
  variant?: 'default' | 'gray';
};

const emit = defineEmits(['blur', 'update:modelValue']);

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  variant: 'default',
});
const picked = ref<typeof props.modelValue>();
const pickedLeft = computed(() => picked.value === props.firstOption.value);
const pickedRight = computed(() => picked.value === props.secondOption.value);

watch(
  () => props.modelValue,
  (newValue) => {
    picked.value = newValue;
  },
  { immediate: true }
);

function setValue(event: Event) {
  const target = event.target as HTMLInputElement;
  const newPicked =
    target.value === 'true'
      ? true
      : target.value === 'false'
        ? false
        : target.value;
  picked.value = newPicked;
  emit('update:modelValue', newPicked);
}
</script>
<style module>
.toggleOption {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-white);
  color: var(--neutral-800);
  user-select: none;

  &:hover {
    background-color: var(--neutral-50);
  }
}

.toggleWrapper {
  display: flex;
  flex-shrink: 0;
  height: 100%;
  line-height: 1;
  border: 1px solid var(--neutral-400);
  border-radius: 2em;
  overflow: hidden;

  & > :first-child {
    padding-left: 1.3em;
    border-right: 1px solid var(--neutral-400);
  }

  & > :last-child {
    padding-right: 1.3em;
  }

  > label {
    padding: 0.5em 1em;
    cursor: pointer;
  }

  &[data-disabled='true'] {
    --color-hover: var(--neutral-50);

    > label {
      cursor: default;
    }
  }
}

.default {
  --color-hover: var(--amber-100);

  .toggleOption.checked {
    background-color: var(--amber-600);
    color: var(--color-white);

    &.disabled {
      background-color: var(--amber-200);
    }
  }
}

.gray {
  --color-hover: var(--neutral-50);

  .toggleOption.checked {
    background-color: var(--neutral-700);
    color: var(--color-white);

    &.disabled {
      background-color: var(--neutral-500);
    }
  }
}
</style>
